import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Link2Off, X } from 'lucide-react';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  InfoHint,
  SegmentedControl,
  Skeleton,
  StatusPill,
  invoiceStatusToPill,
  toast,
  type DataTableColumn,
} from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { RowCheckbox } from '../invoices/bulk';

interface AgreementLine {
  lineId: string;
  invoiceId: string;
  number: string;
  issueDate: string;
  state: string;
  description: string;
  amount: number;
  kind: string;
  agreement: { id: string; name: string; isActive: boolean } | null;
  /** On THIS agreement. */
  linked: boolean;
  /** Not on it, and probably belongs (`agreement-lines.ts`). */
  suggested: boolean;
}

interface AgreementLines {
  window: { from: string; to: string } | null;
  all: boolean;
  truncated: boolean;
  lines: AgreementLine[];
}

type Show = 'all' | 'linked' | 'unlinked';

const cents = (lines: AgreementLine[]) =>
  lines.reduce((s, l) => s + Math.round(l.amount * 100), 0) / 100;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * THE CLIENT'S INVOICES, AS SEEN FROM ONE AGREEMENT.
 *
 * Jesse, 2026-11-04: *"we're going to need to be able to manage linked invoices
 * to agreements … a tab that shows all client invoices and which ones are
 * linked to that agreement."* Every line on this agreement is listed whatever
 * its date (they are what the Invoiced tile is made of); the client's other
 * lines within the agreement's period, or every year. Select lines to link them
 * here or take them off. Either way it moves attribution, never money, and
 * hours a line's invoice bought follow it.
 *
 * Filters live in the URL (`lines`, `years`) beside `tab`.
 */
export function AgreementInvoicesTab({
  agreementId,
  agreementName,
}: {
  agreementId: string;
  agreementName: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showParam = searchParams.get('lines');
  // Opens on the lines ON this agreement — what its Invoiced figure is made of.
  const show: Show = showParam === 'all' || showParam === 'unlinked' ? showParam : 'linked';
  const all = searchParams.get('years') === 'all';
  const patch = (next: Record<string, string | null>) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v === null) params.delete(k);
          else params.set(k, v);
        }
        return params;
      },
      { replace: true },
    );

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['invoices', 'agreement-lines', agreementId, all],
    queryFn: () =>
      api.get<AgreementLines>(
        `/invoices/agreement-lines?agreementId=${encodeURIComponent(agreementId)}${all ? '&all=1' : ''}`,
      ),
  });

  const lines = useMemo(() => query.data?.lines ?? [], [query.data]);
  const linked = lines.filter((l) => l.linked);
  const unlinked = lines.filter((l) => !l.linked);
  const suggested = unlinked.filter((l) => l.suggested);
  const shown = show === 'linked' ? linked : show === 'unlinked' ? unlinked : lines;
  // A selection only ever names rows that are still in the list.
  const chosen = lines.filter((l) => picked.has(l.lineId));
  const toLink = chosen.filter((l) => !l.linked);
  const toUnlink = chosen.filter((l) => l.linked);
  const window = query.data?.window;
  const period =
    window && window.from.slice(0, 4) === window.to.slice(0, 4)
      ? window.from.slice(0, 4)
      : 'this period';

  const refresh = () => {
    setPicked(new Set());
    for (const key of ['invoices', 'agreements', 'support', 'clients']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
  const failed = (e: unknown, what: string) =>
    toast.error(e instanceof ApiError ? e.message : `The lines could not be ${what}.`);

  const link = useMutation({
    mutationFn: (lineIds: string[]) =>
      api.post<{ linked: number; invoices: number }>('/invoices/agreement-lines', {
        agreementId,
        lineIds,
      }),
    onSuccess: (r) => {
      toast.success(
        `${plural(r.linked, 'line')} on ${plural(r.invoices, 'invoice')} linked to ${agreementName}.`,
      );
      refresh();
    },
    onError: (e) => failed(e, 'linked'),
  });
  const unlink = useMutation({
    mutationFn: (lineIds: string[]) =>
      api.post<{ unlinked: number; invoices: number }>('/invoices/agreement-lines/unlink', {
        agreementId,
        lineIds,
      }),
    onSuccess: (r) => {
      toast.success(
        `${plural(r.unlinked, 'line')} on ${plural(r.invoices, 'invoice')} taken off ${agreementName}.`,
      );
      refresh();
    },
    onError: (e) => failed(e, 'unlinked'),
  });
  const busy = link.isPending || unlink.isPending;

  const toggle = (id: string, checked: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  const allShownPicked = shown.length > 0 && shown.every((l) => picked.has(l.lineId));
  const toggleShown = (checked: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      for (const l of shown) {
        if (checked) next.add(l.lineId);
        else next.delete(l.lineId);
      }
      return next;
    });

  const columns: Array<DataTableColumn<AgreementLine>> = [
    {
      key: 'select',
      header: (
        <RowCheckbox
          checked={allShownPicked}
          indeterminate={picked.size > 0}
          disabled={shown.length === 0}
          onToggle={(checked) => toggleShown(checked)}
          label={allShownPicked ? 'Clear the selection' : 'Select every line shown'}
        />
      ),
      render: (l) => (
        <RowCheckbox
          checked={picked.has(l.lineId)}
          onToggle={(checked) => toggle(l.lineId, checked)}
          label={`Select line on invoice #${l.number}`}
        />
      ),
      className: 'w-8 pl-3 pr-1',
    },
    {
      key: 'number',
      header: 'Invoice',
      sortable: true,
      sortValue: (l) => l.number,
      render: (l) => (
        <Link
          to={`/invoices/${l.invoiceId}`}
          className="num font-semibold text-secondary hover:text-accent hover:underline"
        >
          #{l.number}
        </Link>
      ),
    },
    {
      key: 'date',
      header: 'Issued',
      sortable: true,
      sortValue: (l) => l.issueDate,
      render: (l) => <span className="num whitespace-nowrap">{formatDate(l.issueDate)}</span>,
    },
    {
      key: 'state',
      header: 'Status',
      render: (l) => <StatusPill status={invoiceStatusToPill(l.state)} />,
    },
    {
      key: 'line',
      header: 'Line',
      render: (l) => (
        <span className="block max-w-[22rem] truncate text-grey-700" title={l.description}>
          {l.description}
        </span>
      ),
    },
    {
      key: 'agreement',
      header: 'Agreement',
      sortable: true,
      sortValue: (l) => (l.linked ? '' : (l.agreement?.name ?? '~')),
      render: (l) =>
        l.linked ? (
          <span className="inline-flex items-center gap-1 font-semibold text-accent">
            <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            This agreement
          </span>
        ) : (
          <span className="inline-flex max-w-[16rem] items-center gap-1.5 text-grey-500">
            {l.agreement ? (
              <>
                <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate" title={l.agreement.name}>
                  {l.agreement.name}
                </span>
                {!l.agreement.isActive && <span className="shrink-0">(archived)</span>}
              </>
            ) : (
              <>
                <Link2Off className="h-3.5 w-3.5 shrink-0" aria-hidden />
                No agreement
              </>
            )}
            {l.suggested && (
              <span className="shrink-0 rounded bg-accent/10 px-1.5 text-caption font-semibold text-accent">
                suggested
              </span>
            )}
          </span>
        ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      sortable: true,
      sortValue: (l) => l.amount,
      // One line of one invoice: a document figure keeps its cents.
      render: (l) => <span className="num">{formatMoney(l.amount)}</span>,
    },
  ];

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-11 rounded-lg" />
        ))}
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load the invoices"
        message={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <div className="space-y-3">
      <Card padding="sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="text-body-sm text-grey-600">
            <span className="num font-semibold text-secondary">
              {formatMoney(cents(linked), { whole: true })}
            </span>{' '}
            on this agreement · {plural(linked.length, 'line')}
            <InfoHint label="What this list holds" className="ml-1 align-middle">
              <p>
                Every invoice line on {agreementName}, whatever its date — they make up the Invoiced
                figure above. The client&rsquo;s other lines are listed{' '}
                {window && !all
                  ? `from ${formatDate(window.from)} to ${formatDate(window.to)}`
                  : 'for every year'}
                ; void invoices are left out.
              </p>
              <p className="mt-2">
                Linking or unlinking a line changes what it was for, never its amount, its payments
                or its invoice&rsquo;s state. Prepaid hours the invoice bought move with the line.
              </p>
            </InfoHint>
          </p>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            <SegmentedControl
              ariaLabel="Which lines"
              scrollable
              value={show}
              onChange={(v) => patch({ lines: v === 'linked' ? null : v })}
              options={[
                { value: 'linked', label: 'On this agreement', count: linked.length },
                { value: 'unlinked', label: 'Not on it', count: unlinked.length },
                { value: 'all', label: 'All', count: lines.length },
              ]}
            />
            {window && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => patch({ years: all ? null : 'all' })}
              >
                {all ? `Only ${period}` : 'Show every year'}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {suggested.length > 0 && picked.size === 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-accent/30 bg-accent/5 px-3 py-2 text-body-sm">
          <span className="text-grey-700">
            {plural(suggested.length, 'line')} ·{' '}
            <span className="num font-semibold">{formatMoney(cents(suggested), { whole: true })}</span>{' '}
            look like they belong here — they sit on an agreement of the same name.
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setPicked(new Set(suggested.map((l) => l.lineId)));
              // Selected rows must be on screen: suggestions are never on this agreement.
              if (show === 'linked') patch({ lines: 'unlinked' });
            }}
          >
            Select suggested ({suggested.length})
          </Button>
        </div>
      )}

      {picked.size > 0 && (
        <div className="sticky top-16 z-20 rounded-xl bg-secondary px-3 py-2.5 text-white shadow-elevated">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <p className="num flex flex-wrap items-center gap-x-2 text-body-sm font-bold">
              <span>
                {chosen.length} selected
                <span className="ml-2 font-medium text-white/80">
                  {formatMoney(cents(chosen), { whole: true })}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setPicked(new Set())}
                className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-caption font-semibold text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                Clear
              </button>
            </p>
            <div className="flex flex-1 flex-wrap items-center justify-end gap-1.5">
              <Button
                size="sm"
                variant="accent"
                disabled={toLink.length === 0 || busy}
                loading={link.isPending}
                onClick={() => link.mutate(toLink.map((l) => l.lineId))}
              >
                <Link2 className="h-4 w-4" aria-hidden />
                Link {toLink.length || ''} to this agreement
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-white/30 bg-transparent text-white hover:bg-white/10"
                disabled={toUnlink.length === 0 || busy}
                loading={unlink.isPending}
                onClick={() => unlink.mutate(toUnlink.map((l) => l.lineId))}
              >
                <Link2Off className="h-4 w-4" aria-hidden />
                Unlink {toUnlink.length || ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {lines.length === 0 ? (
        <EmptyState
          title="No invoices for this client"
          description={
            window && !all
              ? `Nothing was invoiced to this client in ${period}, and no line is on this agreement.`
              : 'This client has no invoices yet.'
          }
          action={
            window && !all ? (
              <Button variant="outline" onClick={() => patch({ years: 'all' })}>
                Show every year
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card padding="none">
          <DataTable
            columns={columns}
            data={shown}
            rowKey={(l) => l.lineId}
            rowClassName={(l) => (picked.has(l.lineId) ? 'bg-accent/5' : undefined)}
            empty={
              <EmptyState
                title={show === 'linked' ? 'Nothing on this agreement yet' : 'Every line is on this agreement'}
                description={
                  show === 'linked'
                    ? 'Pick lines under Not on it to link them here.'
                    : 'There is nothing else of this client’s to link.'
                }
                action={
                  show === 'linked' && unlinked.length > 0 ? (
                    <Button variant="outline" onClick={() => patch({ lines: 'unlinked' })}>
                      Show {plural(unlinked.length, 'line')} not on it
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </Card>
      )}
      {query.data?.truncated && (
        <p className="text-caption text-grey-500">Showing the 500 most recent lines of each kind.</p>
      )}
    </div>
  );
}
